import { FormProvider, getFormProps } from '@conform-to/react'
import {
	Button,
	Heading,
	Link as RLink,
	Text,
	Flex,
	Box,
} from '@radix-ui/themes'
import { Link } from '@remix-run/react'
import * as React from 'react'
import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import {
	TextField,
	type TextFieldProps,
} from '#app/components/ui/text-field.tsx'
import { type Locale } from '#app/models/locale.ts'
import { Alert } from '#app/routes/($lang)._auth.login/alert.tsx'
import { type App } from '#app/routes/_layout/component.route.tsx'
import {
	ROOT_APP_VIEW,
	type FormEnum,
} from '#app/routes/_layout/route-config.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useLoginForm } from '#app/utils/hooks/forms/use-login-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Login form
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	email = 'email',
	password = 'password',
}

const FORM: FormEnum = {
	id: 'login-form',
	action: '/login',
	fetcherKey: 'login',
	subaction: 'login',
	redirect: '/profile',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.email]: z
			.string({ required_error: 'validation.email.required' })
			.email('validation.email.email')
			.max(254, 'validation.email.max'),
		[INPUTS.password]: z
			.string({ required_error: 'validation.password.required' })
			.min(4, 'validation.password.min')
			.max(254, 'validation.password.max'),
	},
})

type LoginFormProps = {
	app: App
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const LoginForm: React.FC<LoginFormProps> = ({ app, locale, setApp }) => {
	const {
		loginFetcher,
		loginFormValues,
		loginForm,
		loginFields,
		t,
		searchParams,
	} = useLoginForm({ app, setApp })

	return (
		<FormProvider context={loginForm.context}>
			<loginFetcher.Form
				method="post"
				action={`/${locale}${FORM.action}?${searchParams}`}
				{...getFormProps(loginForm)}
				{...(app.options?.preventScrollReset && { preventScrollReset: true })}
			>
				<FormDefaults
					form={loginForm}
					fields={loginFields}
					values={loginFormValues}
				/>
				<Heading align="center" size="8" color="green">
					{t('common.title')}
				</Heading>
				<Box mt="3">
					<Alert t={t} searchParams={searchParams} fetcher={loginFetcher} />
				</Box>
				<Flex direction="column" gapY="6" mt="8">
					{[
						{
							title: INPUTS.email,
							type: 'email',
							validations: {
								required: true,
							},
						},
						{
							title: INPUTS.password,
							type: 'password',
							validations: {
								required: true,
								minLength: 4,
							},
						},
					].map(({ title, type, validations }) => (
						<TextField
							key={title}
							type={type as TextFieldProps['type']}
							meta={loginFields[title]}
							t={t}
							validations={validations}
						>
							{t(`label.${type}`)}
						</TextField>
					))}
					<Flex direction="column" gap="3" mt="3">
						<Flex justify="end">
							<Link
								to={`/${locale}/reset-password${loginFields[INPUTS['email']].value ? `?email=${loginFields[INPUTS['email']].value}` : ''}`}
								prefetch="intent"
								onClick={(e) => {
									if (app.displayModal) {
										e.preventDefault()
										setApp((prev) => ({
											...prev,
											modalView: ROOT_APP_VIEW.resetPasswordView,
											options: {
												...prev.options,
												data: {
													[INPUTS.email]:
														loginFields[INPUTS['email']].value ||
														('' as string),
												},
											},
										}))
									}
								}}
							>
								<RLink asChild weight="bold" size="2">
									<span>{t('button.resetPassword')}</span>
								</RLink>
							</Link>
						</Flex>
						<Button size="3" type="submit">
							<SubmissionButton fetcher={loginFetcher}>
								{t('button.loginSubmit')}
							</SubmissionButton>
						</Button>
						<Flex align="center" justify="center" gapX="2" mt="4">
							<Text color="gray">{t('common.registration')}</Text>
							<Link
								to={`/${locale}/registration${loginFields[INPUTS['email']].value ? `?email=${loginFields[INPUTS['email']].value}` : ''}`}
								prefetch="intent"
								onClick={(e) => {
									if (app.displayModal) {
										e.preventDefault()
										setApp((prev) => ({
											...prev,
											modalView: ROOT_APP_VIEW.registrationView,
											options: {
												...prev.options,
												data: {
													[INPUTS.email]:
														loginFields[INPUTS['email']].value ||
														('' as string),
												},
											},
										}))
									}
								}}
							>
								<RLink asChild weight="bold" size="2">
									<span>{t('button.registration')}</span>
								</RLink>
							</Link>
						</Flex>
					</Flex>
				</Flex>
			</loginFetcher.Form>
		</FormProvider>
	)
}
LoginForm.displayName = 'LoginForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type LoginFormProps,
	LoginForm,
	INPUTS as LOGIN_INPUTS,
	FORM as LOGIN_FORM,
	schema as loginSchema,
}
